import * as THREE from 'three';
import { createScene } from './ui/scene.js';
import { BoardView } from './ui/board.js';
import { createSetupScreen } from './ui/setup.js';
import { createStageSelectScreen } from './ui/stageSelect.js';
import { STAGES, nextStage } from './stages/stages.js';
import { saveStageCleared, isStageCleared } from './ui/stageProgress.js';
import {
  ABILITY_LABELS,
  abilityMovesOf,
  abilityTargetCells,
  isPassOnly,
} from './ui/moveFilters.js';
import {
  createCpuDriver,
  cpuShouldAct,
  humanCanAct,
  isCpuKind,
  isSpectateMatch,
  PLAYER_KIND_LABELS,
  SPECTATE_SPEEDS,
  DEFAULT_SPEED_ID,
  speedById,
} from './ui/cpu.js';
import { seForMove } from './ui/sfx.js';
import { createAiRunner } from './ui/aiRunner.js';
import { createWallInput, createTapTracker, isTouchLike } from './ui/wallInput.js';
import { guideMessage } from './ui/guide.js';
import { createFlow, derive, resignWinner } from './ui/flow.js';
import { createTutorial, allowedMoveTypes } from './tutorial/index.js';
import {
  moveForCellTap,
  moveForWallSlot,
  moveForWallPick,
  submitToTutorial,
  shouldSnapBoard,
} from './ui/tutorialFlow.js';
import { winProbability } from './ai/winprob.js';
import {
  createGame,
  legalMoves,
  pawnMoves,
  isLegal,
  applyMove,
} from './core/index.js';
import { createAudio } from './audio/index.js';
import { characterDef } from './core/characters.js';

const appEl = document.getElementById('app');
const canvas = document.getElementById('game-canvas');
const hudEl = document.getElementById('hud');
const hudToggleBtn = document.getElementById('hud-toggle');
const turnPlayerEl = document.getElementById('turn-player');
const cpuIndicatorEl = document.getElementById('cpu-indicator');
const playerCards = [
  document.getElementById('player-card-p0'),
  document.getElementById('player-card-p1'),
];
const charEls = [document.getElementById('char-p0'), document.getElementById('char-p1')];
const ctrlEls = [document.getElementById('ctrl-p0'), document.getElementById('ctrl-p1')];
const wallsEls = [document.getElementById('walls-p0'), document.getElementById('walls-p1')];
const abilityEls = [document.getElementById('ability-p0'), document.getElementById('ability-p1')];
const swampEls = [document.getElementById('swamp-p0'), document.getElementById('swamp-p1')];
const hexEls = [document.getElementById('hex-p0'), document.getElementById('hex-p1')];
const legendEl = document.getElementById('legend');
const abilityBtn = document.getElementById('ability-btn');
const passBtn = document.getElementById('pass-btn');
const modeIndicatorEl = document.getElementById('mode-indicator');
const bannerEl = document.getElementById('winner-banner');
const winnerTextEl = document.getElementById('winner-text');
const rematchBtn = document.getElementById('rematch-btn');
const toSetupBtn = document.getElementById('to-setup-btn');
const nextStageBtn = document.getElementById('next-stage-btn');
const stageRetryBtn = document.getElementById('stage-retry-btn');
const toStageSelectBtn = document.getElementById('to-stage-select-btn');
const setupRootEl = document.getElementById('setup-overlay');
const stageSelectRootEl = document.getElementById('stage-select-overlay');
const spectateControlsEl = document.getElementById('spectate-controls');
const pauseBtn = document.getElementById('pause-btn');
const speedButtonsEl = document.getElementById('speed-buttons');
const muteBtn = document.getElementById('mute-btn');
const guideBarEl = document.getElementById('guide-bar');
const wallConfirmEl = document.getElementById('wall-confirm');
const wallConfirmOkBtn = document.getElementById('wall-confirm-ok');
const wallConfirmCancelBtn = document.getElementById('wall-confirm-cancel');
const meterEl = document.getElementById('advantage-meter');
const meterFillEl = document.getElementById('meter-fill');
const meterPctP0El = document.getElementById('meter-pct-p0');
const meterPctP1El = document.getElementById('meter-pct-p1');
const titleScreenEl = document.getElementById('title-screen');
const titleSingleBtn = document.getElementById('title-single-btn');
const titleCustomBtn = document.getElementById('title-custom-btn');
const menuBtn = document.getElementById('menu-btn');
const gameMenuEl = document.getElementById('game-menu');
const menuResumeBtn = document.getElementById('menu-resume-btn');
const menuResignBtn = document.getElementById('menu-resign-btn');
const menuQuitSpectateBtn = document.getElementById('menu-quit-spectate-btn');
const menuRematchBtn = document.getElementById('menu-rematch-btn');
const menuSetupBtn = document.getElementById('menu-setup-btn');
const menuTitleBtn = document.getElementById('menu-title-btn');
const titleTutorialBtn = document.getElementById('title-tutorial-btn');
const menuQuitTutorialBtn = document.getElementById('menu-quit-tutorial-btn');
const tutorialPanelEl = document.getElementById('tutorial-panel');
const tutProgressEl = document.getElementById('tut-progress');
const tutTitleEl = document.getElementById('tut-title');
const tutDotsEl = document.getElementById('tut-dots');
const tutSkipBtn = document.getElementById('tut-skip-btn');
const tutIntroEl = document.getElementById('tut-intro');
const tutorialToastEl = document.getElementById('tutorial-toast');
const tutorialCompleteEl = document.getElementById('tutorial-complete');
const tutCompleteTextEl = document.getElementById('tut-complete-text');
const tutCpuBtn = document.getElementById('tut-cpu-btn');
const tutTitleBtn = document.getElementById('tut-title-btn');

const { renderer, scene, camera, controls, resize } = createScene(canvas);

// --- サウンド（シングルトン。unlock は必ずユーザージェスチャー内で） ---
const audio = createAudio();
// 保険: 最初のポインタ操作で unlock（セットアップ画面のクリック音も鳴らせる）
window.addEventListener('pointerdown', () => audio.unlock(), { once: true });

// 対局設定（セットアップ画面の出力。再戦「もう一度」で再利用）
let settings = {
  players: ['human', 'human'],
  characters: ['none', 'none'],
  gimmicks: [],
  meter: true, // 形勢メーター表示（セットアップで選択、localStorage に永続化）
};
let state = createGame(settings); // タイトル/セットアップ中の背景用にも1局作っておく
const board = new BoardView(scene, state.config.boardSize);

let legal = [];        // legalMoves(state) のキャッシュ（毎手更新）
let moves = [];        // 手番プレイヤーの移動可能マス（緑ハイライト・クリック判定共用）
let abilityMode = false; // 能力モード（対象マス/壁のクリックで発動）

// 画面遷移の状態機械（タイトル/セットアップ/対局/チュートリアル + メニュー・投了。
// 遷移ロジックと表示可否の導出は src/ui/flow.js、純粋JS）
const flow = createFlow();
const flowUi = () => derive(flow.snapshot());

// チュートリアルエンジン（src/tutorial/）。tutorial 画面でのみ非 null。
let tut = null;
const inTutorial = () => flow.screen === 'tutorial';

// シングルモード（ステージ）で対局中のステージ。カスタム/チュートリアルでは
// null。tut と同様に「今どのモードで対局しているか」を表す UI 層の変数で、
// core state には一切影響しない。
let currentStage = null;
const inSingleMode = () => flow.screen === 'game' && currentStage !== null;

// 勝敗の実効値: core の勝敗 or UI 層の投了（core state は投了では触らない）
function effectiveWinner() {
  return state.winner !== null ? state.winner : flow.resignedWinner;
}

// カメラの自動旋回はタイトル画面の演出のみ（reduced-motion では無効）
function updateAutoRotate() {
  controls.autoRotate =
    flow.screen === 'title' &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
controls.autoRotateSpeed = 0.7;
// 交点付近で h/v が曖昧なとき直前の向きを維持するためのキャッシュ
let lastOrientation = null;

// 壁設置入力の状態機械（タッチ/ペン=2段階確定、マウス=即設置。純粋JS）
const wallInput = createWallInput();
// タップ/ドラッグ判別（カメラ操作と盤操作の切り分け）
const tapTracker = createTapTracker();
// タッチの溝ピッキング許容量（マスピッチ比）。指は不正確なのでマウス(既定0.04)より寛容に。
const TOUCH_WALL_SLACK = 0.16;

// 確定UI（✓ 置く / ✕ やめる）: タッチ候補プレビュー中のみ表示
function updateWallConfirmUi() {
  const c = wallInput.candidate;
  wallConfirmEl.classList.toggle('hidden', !c);
  if (c) wallConfirmOkBtn.classList.toggle('illegal', !c.legal);
  updateGuideBar();
}

// --- 画面下の常設操作ガイドバー（文言決定は src/ui/guide.js、純粋JS） ---
// タッチ系（coarse pointer）かどうかで通常時の基本操作説明を出し分ける。
const coarsePointerQuery = window.matchMedia('(pointer: coarse)');

function updateGuideBar() {
  if (!flowUi().guideAllowed) {
    guideBarEl.classList.add('hidden');
    return;
  }
  // チュートリアル: 現在ステップの instruction を最優先で表示。
  // 全レッスン完了後は完了オーバーレイに委ねてバーを消す。
  const tutText = inTutorial() && tut && !tut.isFinished ? tut.step.instruction : null;
  if (inTutorial() && !tutText) {
    guideBarEl.classList.add('hidden');
    return;
  }
  const cur = state.players[state.current];
  const msg = guideMessage({
    tutorialText: tutText,
    winner: effectiveWinner(),
    cpuTurn:
      !inTutorial() &&
      state.winner === null &&
      isCpuKind(settings.players[state.current]),
    cpuPaused: cpuDriver.paused,
    wallPreview: !!wallInput.candidate,
    abilityMode,
    abilityKind: abilityMode ? (currentAbility()?.id ?? null) : null,
    swamped: !!(cur.effects && cur.effects.swamped),
    coarse: coarsePointerQuery.matches,
  });
  guideBarEl.textContent = msg;
  guideBarEl.classList.toggle('hidden', msg === '');
}

// 候補解除の共通経路（✕ボタン・能力モード移行・マウス操作への切替時）
function clearWallCandidate() {
  wallInput.cancel();
  board.hideWallPreview();
  updateWallConfirmUi();
}

// --- CPU 対局の駆動（タイマー状態機械は src/ui/cpu.js、純粋JS） ---
// AI 思考は Web Worker（src/ui/aiWorker.js）へ委譲し、メインスレッド／
// レンダーループをブロックしない。Worker 不可の環境では aiRunner が
// 同期実行へ自動フォールバックする。Worker は 1 個を対局をまたいで使い回す。
const aiRunner = createAiRunner();
const cpuDriver = createCpuDriver();

const setupScreen = createSetupScreen(
  setupRootEl,
  (newSettings) => {
    settings = newSettings;
    audio.unlock();   // クリックハンドラ内: 自動再生ポリシー解除
    audio.startBgm(); // 多重呼び出し安全
    startGame();
  },
  {
    onSelect: () => audio.playSe('select'),
    onBack: () => goToTitle(),
  },
);

// シングルモードのステージ選択画面。クリック時に startStage、戻るでタイトルへ。
// クリア状況は show() のたびに localStorage から読み直す。
const stageSelectScreen = createStageSelectScreen(
  stageSelectRootEl,
  STAGES,
  (stage) => {
    audio.unlock();
    audio.startBgm();
    startStage(stage);
  },
  () => goToTitle(),
  {
    onSelect: () => audio.playSe('select'),
    isCleared: (id) => isStageCleared(id),
  },
);

function startGame() {
  cpuDriver.reset(); // 前の対局のCPUタイマーを全て無効化（generation +1）
  tut = null;
  currentStage = null; // カスタム/通常対局: シングルモードを抜ける
  hideTutorialToast();
  state = createGame({
    characters: settings.characters,
    gimmicks: settings.gimmicks,
  });
  // CPU の AI は対局開始時に各サイド1個生成し、対局中は使い回す（再戦時は
  // 作り直し）。レベル/seed の変更はこの configure（= init 再送）で反映される。
  aiRunner.configure(
    settings.players.map((kind, i) =>
      isCpuKind(kind) ? { level: kind, seed: Date.now() + i * 7919 } : null,
    ),
  );
  board.setGimmicks(state.config.gimmicks);
  lastOrientation = null;
  flow.startGame();
  applyFlowUi();
  updatePauseBtn();
  refresh();
}

// シングルモード: プリセット局面（stage.config.position）から対局を開始する。
// 通常の 'game' 画面・対局ループをそのまま流用し、プレイヤー=先手(人間)、
// CPU=後手(stage.cpu.level) を設定する。currentStage を立てることで勝敗
// バナーの導線（次のステージ / リトライ / ステージ選択へ）が切り替わる。
function startStage(stage) {
  cpuDriver.reset();
  tut = null;
  currentStage = stage;
  hideTutorialToast();
  // settings はシングルモード用に組み替える（メーター表示の好みは維持）。
  settings = {
    ...settings,
    players: ['human', stage.cpu.level],
    characters: [...(stage.config.characters ?? ['none', 'none'])],
    gimmicks: (stage.config.gimmicks ?? []).map((g) => ({ ...g })),
  };
  // createGame が position を検証・適用（不正なら throw）。stage データは
  // ビルド時テストで検証済みなので、ここでの throw は想定しない。
  state = createGame(stage.config);
  aiRunner.configure(
    settings.players.map((kind, i) =>
      isCpuKind(kind) ? { level: kind, seed: Date.now() + i * 7919 } : null,
    ),
  );
  board.setGimmicks(state.config.gimmicks);
  lastOrientation = null;
  flow.startGame();
  applyFlowUi();
  updatePauseBtn();
  refresh();
}

// 画面遷移後に毎回呼ぶ: flow の状態を各オーバーレイ/ボタンの表示へ反映
function applyFlowUi() {
  const ui = flowUi();
  titleScreenEl.classList.toggle('hidden', !ui.titleVisible);
  hudEl.classList.toggle('hidden', ui.titleVisible); // タイトルでは HUD ごと隠す
  appEl.classList.toggle('tutorial', ui.tutorialVisible); // HUD 簡略化（CSS）
  if (ui.setupVisible) setupScreen.show();
  else setupScreen.hide();
  if (ui.stageSelectVisible) stageSelectScreen.show();
  else stageSelectScreen.hide();
  menuBtn.classList.toggle('hidden', !ui.menuButtonVisible);
  updateMenuUi();
  updateTutorialPanel();
  updateSpectateControls();
  updateAutoRotate();
  updateGuideBar();
}

// セットアップ / タイトルへ戻る共通経路（CPUタイマーは必ず無効化）
function goToSetup() {
  cpuDriver.reset();
  tut = null;
  currentStage = null;
  hideTutorialToast();
  flow.toSetup();
  applyFlowUi();
  refresh(); // ハイライト・バナー・メーターを片づける
}

// シングルモードのステージ選択画面へ。
function goToStageSelect() {
  cpuDriver.reset();
  tut = null;
  currentStage = null;
  hideTutorialToast();
  flow.toStageSelect();
  applyFlowUi();
  refresh();
}

function goToTitle() {
  cpuDriver.reset();
  tut = null;
  currentStage = null;
  hideTutorialToast();
  flow.toTitle();
  // タイトルの背景は初期配置の盤面
  state = createGame({
    characters: settings.characters,
    gimmicks: settings.gimmicks,
  });
  board.setGimmicks(state.config.gimmicks);
  lastOrientation = null;
  applyFlowUi();
  refresh();
}

// --- チュートリアルモード ---
// 盤描画・入力・SE は通常対局の機構をそのまま流用し、着手だけを
// applyMove の代わりにチュートリアルエンジンの tryMove へ流す。
// Move 化と結果→副作用の変換は src/ui/tutorialFlow.js（純粋JS）。

function startTutorial() {
  cpuDriver.reset(); // tutorial に CPU 駆動はない（保険で全タイマー無効化）
  currentStage = null;
  tut = createTutorial();
  lastOrientation = null;
  flow.toTutorial();
  applyFlowUi();
  refreshTutorial();
}

// チュートリアルの状態変化後に毎回呼ぶ（refresh() の tutorial 版）。
function refreshTutorial() {
  if (!tut) return;
  abilityMode = false;
  const prev = state;
  state = tut.state; // エンジンの state を通常機構でそのまま描画
  board.setGimmicks(state.config.gimmicks);
  const finished = tut.isFinished;
  const h = (!finished && tut.step.highlight) || {};
  legal = finished ? [] : legalMoves(state);
  moves = (h.cells ?? []).map((c) => ({ col: c.col, row: c.row }));
  // レッスン/ステップの setup で局面が作り直されたときは snap（アニメなし）
  board.sync(state, moves, 'move', { snap: shouldSnapBoard(prev, state) });
  board.setTutorialHighlights(finished ? null : h);
  // レッスン1の「ゴール」の説明と噛み合うよう、チュートリアル中も
  // 手番（=プレイヤー）のゴール表示を出す。完了後は停止。
  board.setGoalHighlight(finished ? null : state.current);
  board.hideWallPreview();
  board.highlightWall(null);
  wallInput.reset();
  updateWallConfirmUi();
  updateHud();
  updateActionButtons();
  updateMeter();
  updateCpuIndicator();
  updateTutorialPanel();
  updateGuideBar();
}

// 指示パネル（レッスン名 + intro + 進捗ドット）と完了オーバーレイの表示。
function updateTutorialPanel() {
  const active = inTutorial() && !!tut;
  const finished = active && tut.isFinished;
  tutorialPanelEl.classList.toggle('hidden', !active || finished);
  tutorialCompleteEl.classList.toggle('hidden', !finished);
  if (finished) {
    tutCompleteTextEl.textContent = tut.step.instruction; // 卒業メッセージ
    return;
  }
  if (!active) return;
  const p = tut.progress;
  tutProgressEl.textContent = `レッスン ${p.lessonIndex + 1}/${p.lessonCount}`;
  tutTitleEl.textContent = tut.step.lessonTitle;
  tutIntroEl.textContent = tut.step.lessonIntro;
  tutDotsEl.textContent = '';
  for (let i = 0; i < p.stepCount; i++) {
    const dot = document.createElement('span');
    dot.className =
      'dot' + (i < p.stepIndex ? ' done' : i === p.stepIndex ? ' now' : '');
    tutDotsEl.appendChild(dot);
  }
}

// wrong / blocked / 成功メッセージのトースト（ガイドバーとは別、2.6秒で消える）
let tutorialToastTimer = null;

function showTutorialToast(message, success = false) {
  if (!message) return;
  tutorialToastEl.textContent = message;
  tutorialToastEl.classList.toggle('success', success);
  tutorialToastEl.classList.remove('hidden');
  tutorialToastEl.classList.remove('show');
  void tutorialToastEl.offsetWidth; // 連続表示でもフェードが効くよう reflow
  tutorialToastEl.classList.add('show');
  clearTimeout(tutorialToastTimer);
  tutorialToastTimer = setTimeout(
    () => tutorialToastEl.classList.remove('show'),
    2600,
  );
}

function hideTutorialToast() {
  clearTimeout(tutorialToastTimer);
  tutorialToastEl.classList.remove('show');
}

// 盤入力から作った Move をエンジンへ。advance = SE + 盤更新 + 次ステップ、
// wrong / blocked = 優しい選択音 + メッセージトースト（状態は進まない）。
function submitTutorialMove(move) {
  if (!tut) return null;
  const res = submitToTutorial(tut, move);
  if (res.result === 'advance') {
    if (res.se) audio.playSe(res.se);
    if (res.message) showTutorialToast(res.message, true);
    refreshTutorial();
    if (res.done) audio.playSe('win'); // 全レッスン完了のファンファーレ
  } else {
    audio.playSe(res.se); // illegal SE は使わない（select などの優しい音）
    showTutorialToast(res.message);
  }
  return res;
}

// 手番プレイヤーの ability（残回数あり）を返す。なければ null。
function currentAbility() {
  const ab = state.players[state.current].ability;
  return ab && ab.usesLeft > 0 ? ab : null;
}

// 人間がいま盤面を操作してよいか
// （CPU手番・勝敗確定/投了・対局画面以外・メニュー表示中は不可。
// チュートリアルは常に人間の入力で、正誤判定はエンジンに委ねる）
function isHumanTurn() {
  if (!flowUi().boardInputAllowed) return false;
  if (inTutorial()) return !!tut;
  return humanCanAct(state, settings.players);
}

// 着手の共通経路: applyMove → SE → refresh（人間・CPU 双方から使う）
function commitMove(move) {
  const prev = state;
  state = applyMove(state, move);
  const se = seForMove(prev, state);
  if (se) audio.playSe(se);
  refresh();
}

// 状態変化後に毎回呼ぶ。手番がCPUなら思考ディレイ後に自動着手を予約する。
// タイマーは cpuDriver が一元管理（常に張り替え・古い generation は発火しない）。
function driveCpu() {
  cpuDriver.maybeSchedule(
    !flowUi().cpuHold && cpuShouldAct(state, settings.players),
    () => {
      // 思考ディレイ経過後に Worker へ思考を依頼し、応答が届いたら着手する
      // （直列方式。ディレイ 300〜1600ms に対し思考は最大 ~130ms なので
      // 体感差は小さく、二重リクエスト管理が不要になる）。
      // 古い応答の破棄は二重: aiRunner が requestId で最新リクエスト以外を
      // 捨て、ここでは generation / paused / cpuHold（「もう一度」「設定を
      // 変える」「一時停止」「メニュー」「投了」後）を確認してから commit する。
      const gen = cpuDriver.generation;
      aiRunner.request(state.current, state, (move) => {
        if (gen !== cpuDriver.generation || cpuDriver.paused || flowUi().cpuHold) {
          return;
        }
        commitMove(move);
      });
    },
  );
  updateCpuIndicator();
}

function refresh() {
  abilityMode = false;
  // タイトル/セットアップの背景・メニュー中・決着後（投了含む）は
  // ハイライトなしの素の盤面を見せる
  const live = flow.screen === 'game' && effectiveWinner() === null;
  legal = live ? legalMoves(state) : [];
  moves = live ? pawnMoves(state, state.current) : [];
  board.sync(state, moves, 'move');
  // 手番プレイヤーのゴール側の帯を明滅 + シェブロン表示。
  // タイトル/セットアップ/勝敗確定時は停止（帯は静的表示のまま）。
  board.setGoalHighlight(live ? state.current : null);
  board.setTutorialHighlights(null); // チュートリアル画面以外の共通経路
  board.hideWallPreview();
  board.highlightWall(null);
  wallInput.reset(); // 着手確定・対局リセットで壁候補は必ず破棄
  updateWallConfirmUi();
  updateHud();
  updateActionButtons();
  updateMeter();
  driveCpu();
}

function updateCpuIndicator() {
  const thinking =
    !flowUi().cpuHold &&
    state.winner === null &&
    isCpuKind(settings.players[state.current]) &&
    !cpuDriver.paused;
  cpuIndicatorEl.classList.toggle('hidden', !thinking);
  updateGuideBar();
}

function updateHud() {
  const isP0 = state.current === 0;
  turnPlayerEl.textContent = isP0 ? '先手' : '後手';
  turnPlayerEl.className = isP0 ? 'player-1' : 'player-2';

  // 手番側のプレイヤーカードを強調（発光）
  const activeIdx = effectiveWinner() === null ? state.current : -1;
  playerCards[0].classList.toggle('active', activeIdx === 0);
  playerCards[1].classList.toggle('active', activeIdx === 1);

  for (let i = 0; i < 2; i++) {
    const p = state.players[i];
    charEls[i].textContent = characterDef(p.character).name;
    const kind = settings.players[i];
    ctrlEls[i].textContent = isCpuKind(kind) ? PLAYER_KIND_LABELS[kind] : '';
    ctrlEls[i].classList.toggle('hidden', !isCpuKind(kind));
    wallsEls[i].textContent = p.wallsLeft;
    if (p.ability) {
      const label = ABILITY_LABELS[p.ability.id] ?? p.ability.id;
      abilityEls[i].textContent = `${label} 残${p.ability.usesLeft}回`;
    } else {
      abilityEls[i].textContent = '';
    }
    swampEls[i].classList.toggle('hidden', !(p.effects && p.effects.swamped));
    hexEls[i].classList.toggle('hidden', !(p.effects && p.effects.hexed));
  }

  legendEl.classList.toggle('hidden', state.config.gimmicks.length === 0);

  const winner = effectiveWinner();
  if (winner !== null && flow.screen === 'game') {
    const p0Won = winner === 0;
    const single = inSingleMode();
    // 投了決着（core の winner は null のまま）は明示する
    const resigned = state.winner === null;
    if (single) {
      // シングルモード: プレイヤーは常に先手。クリア/失敗の文言にする。
      winnerTextEl.textContent = p0Won ? 'ステージクリア！' : 'ステージ失敗…';
      if (p0Won) saveStageCleared(currentStage.id); // 重複記録は無視される
    } else {
      winnerTextEl.textContent = resigned
        ? p0Won
          ? '後手投了 — 先手の勝ち！'
          : '先手投了 — 後手の勝ち！'
        : p0Won
          ? '先手の勝ち！'
          : '後手の勝ち！';
    }
    winnerTextEl.className = p0Won ? 'player-1' : 'player-2';
    // バナーの導線: 通常は「もう一度/設定」、シングルは「次へ/リトライ/選択へ」
    rematchBtn.classList.toggle('hidden', single);
    toSetupBtn.classList.toggle('hidden', single);
    nextStageBtn.classList.toggle(
      'hidden',
      !(single && p0Won && nextStage(currentStage.id) !== null),
    );
    stageRetryBtn.classList.toggle('hidden', !single);
    toStageSelectBtn.classList.toggle('hidden', !single);
    bannerEl.classList.remove('hidden');
  } else {
    bannerEl.classList.add('hidden');
  }
}

// --- 形勢メーター（推定は src/ai/winprob.js、約0.03ms/回なので毎手呼ぶ） ---

function updateMeter() {
  const visible = flowUi().meterAllowed && settings.meter !== false;
  meterEl.classList.toggle('hidden', !visible);
  appEl.classList.toggle('with-meter', visible); // HUD を下げて重なり回避
  if (!visible) return;
  const winner = effectiveWinner();
  // 決着（投了含む）は 100/0。それ以外は推定勝率。
  const p0 = winner !== null ? (winner === 0 ? 1 : 0) : winProbability(state).p0;
  const pct = Math.round(p0 * 100);
  meterFillEl.style.width = `${pct}%`;
  meterPctP0El.textContent = `${pct}%`;
  meterPctP1El.textContent = `${100 - pct}%`;
}

function updateActionButtons() {
  const humanTurn = isHumanTurn();
  const ab = currentAbility();
  const abilityLegal = abilityMovesOf(legal);
  // チュートリアル中: エンジンの expect が能力手を求めるステップでだけ活性化
  const tutAbilityOk =
    !inTutorial() ||
    (tut && !tut.isFinished && allowedMoveTypes(tut.step.expect).includes('ability'));

  if (humanTurn && ab) {
    const label = ABILITY_LABELS[ab.id] ?? ab.id;
    abilityBtn.textContent = abilityMode
      ? `能力モード解除（Esc）`
      : `能力を使う: ${label}（残り${ab.usesLeft}回）`;
    // 合法な能力手が存在するときだけ活性化
    abilityBtn.disabled = abilityLegal.length === 0 || !tutAbilityOk;
    abilityBtn.classList.toggle('active', abilityMode);
    abilityBtn.classList.remove('hidden');
  } else {
    abilityBtn.classList.add('hidden');
    abilityBtn.classList.remove('active');
  }

  // パスは「唯一の合法手」のときだけ表示（CPU手番は自動で指すので非表示。
  // チュートリアルではパスを求めるレッスンはないため常に非表示）
  passBtn.classList.toggle('hidden', !humanTurn || !isPassOnly(legal) || inTutorial());

  // 操作説明はガイドバーに一本化。ここでは「能力モード中」の明示のみ。
  if (abilityMode && ab) {
    const label = ABILITY_LABELS[ab.id] ?? ab.id;
    modeIndicatorEl.textContent = `能力モード: ${label}`;
    modeIndicatorEl.classList.remove('hidden');
  } else {
    modeIndicatorEl.classList.add('hidden');
  }
}

// 能力モードの切り替え。対象マス(紫)/壁ホバー強調に表示を切り替える。
function setAbilityMode(on) {
  const next =
    !!on && isHumanTurn() && abilityMovesOf(legal).length > 0 && state.winner === null;
  if (next === abilityMode) {
    updateActionButtons();
    return;
  }
  abilityMode = next;
  clearWallCandidate(); // 壁候補プレビューと能力対象表示は同時に出さない
  board.highlightWall(null);
  if (abilityMode) {
    // 対象マス（wallJump / diagonalStep）。removeWall はマス対象なしで壁ホバーへ。
    board.sync(state, abilityTargetCells(legal), 'ability');
  } else {
    board.sync(state, moves, 'move');
  }
  updateActionButtons();
  updateGuideBar();
}

abilityBtn.addEventListener('click', () => {
  audio.playSe('select');
  setAbilityMode(!abilityMode);
});

passBtn.addEventListener('click', () => {
  if (!isHumanTurn()) return;
  const move = { type: 'pass' };
  if (isLegal(state, move)) commitMove(move);
});

window.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  if (flow.menuOpen) closeMenu(); // メニュー優先で閉じる
  else setAbilityMode(false);
});

rematchBtn.addEventListener('click', () => {
  audio.playSe('select');
  startGame(); // 同じ settings で再戦（CPUタイマーは startGame 内で無効化）
});

toSetupBtn.addEventListener('click', () => {
  audio.playSe('select');
  goToSetup();
});

// --- シングルモード（勝敗バナーの導線） ---

nextStageBtn.addEventListener('click', () => {
  audio.playSe('select');
  const next = currentStage && nextStage(currentStage.id);
  if (next) startStage(next);
});

stageRetryBtn.addEventListener('click', () => {
  audio.playSe('select');
  if (currentStage) startStage(currentStage);
});

toStageSelectBtn.addEventListener('click', () => {
  audio.playSe('select');
  goToStageSelect();
});

// --- タイトル画面 ---

titleSingleBtn.addEventListener('click', () => {
  audio.unlock(); // クリックハンドラ内: 自動再生ポリシー解除
  audio.playSe('select');
  goToStageSelect();
});

titleCustomBtn.addEventListener('click', () => {
  audio.unlock(); // クリックハンドラ内: 自動再生ポリシー解除
  audio.playSe('select');
  goToSetup();
});

// --- チュートリアル（入口・スキップ・離脱・完了画面） ---

titleTutorialBtn.addEventListener('click', () => {
  audio.unlock(); // クリックハンドラ内: 自動再生ポリシー解除
  audio.playSe('select');
  audio.startBgm(); // 多重呼び出し安全
  startTutorial();
});

tutSkipBtn.addEventListener('click', () => {
  if (!tut) return;
  audio.playSe('select');
  hideTutorialToast();
  tut.skipLesson();
  refreshTutorial();
});

menuQuitTutorialBtn.addEventListener('click', () => {
  audio.playSe('select');
  goToTitle();
});

// 完了画面: キャラなし・クラシック・人間 vs CPU（よわい）で対局を直接開始
tutCpuBtn.addEventListener('click', () => {
  audio.unlock();
  audio.playSe('select');
  audio.startBgm();
  settings = {
    ...settings,
    players: ['human', 'easy'],
    characters: ['none', 'none'],
    gimmicks: [],
  };
  startGame();
});

tutTitleBtn.addEventListener('click', () => {
  audio.playSe('select');
  goToTitle();
});

// --- 対局中メニュー（☰）---
// 表示中は flow.menuOpen が立ち、cpuHold（CPUタイマー保留）と
// boardInputAllowed=false（盤入力ロック）が derive() から導かれる。

function updateMenuUi() {
  const ui = flowUi();
  gameMenuEl.classList.toggle('hidden', !ui.menuVisible);
  if (!ui.menuVisible) return;
  const tutorial = inTutorial();
  // チュートリアル中のメニューは「つづける / チュートリアルをやめる」だけ
  menuQuitTutorialBtn.classList.toggle('hidden', !tutorial);
  menuRematchBtn.classList.toggle('hidden', tutorial);
  menuSetupBtn.classList.toggle('hidden', tutorial);
  menuTitleBtn.classList.toggle('hidden', tutorial);
  const spectate = !tutorial && isSpectateMatch(settings.players);
  const over = effectiveWinner() !== null;
  // 投了は人間が1人以上いる進行中の対局のみ。観戦は「観戦をやめる」に置換。
  menuResignBtn.classList.toggle('hidden', tutorial || spectate || over);
  menuQuitSpectateBtn.classList.toggle('hidden', !spectate || over);
  // シングルモードでは「もう一度→リトライ」「設定を変える→ステージ選択へ」
  const single = inSingleMode();
  menuRematchBtn.textContent = single ? 'リトライ' : 'もう一度';
  menuSetupBtn.textContent = single ? 'ステージ選択へ' : '設定を変える';
}

function openMenu() {
  if (!flow.openMenu()) return;
  clearWallCandidate(); // タッチの壁候補プレビューは片づけてから
  applyFlowUi();
  driveCpu(); // cpuHold: 保留中のCPU思考タイマーをキャンセル
}

function closeMenu() {
  flow.closeMenu();
  applyFlowUi();
  driveCpu(); // CPU手番なら思考タイマーを張り直す
}

menuBtn.addEventListener('click', () => {
  audio.playSe('select');
  if (flow.menuOpen) closeMenu();
  else openMenu();
});

menuResumeBtn.addEventListener('click', () => {
  audio.playSe('select');
  closeMenu();
});

menuResignBtn.addEventListener('click', () => {
  audio.playSe('select');
  const winner = resignWinner(settings.players.map(isCpuKind), state.current);
  if (winner === null) return; // 観戦（ボタン非表示のはずだが二重防御）
  if (!window.confirm('投了しますか？（相手の勝ちになります）')) return;
  if (!flow.resign(winner)) return;
  audio.playSe('win');
  applyFlowUi(); // メニューを閉じる
  refresh();     // 勝利バナー表示・入力ロック・CPU停止・メーター100/0
});

menuQuitSpectateBtn.addEventListener('click', () => {
  audio.playSe('select');
  goToTitle();
});

menuRematchBtn.addEventListener('click', () => {
  audio.playSe('select');
  // シングルモード中は同ステージをリトライ（flow.startGame がメニューを閉じる）
  if (inSingleMode()) startStage(currentStage);
  else startGame(); // 同設定で再戦
});

menuSetupBtn.addEventListener('click', () => {
  audio.playSe('select');
  if (inSingleMode()) goToStageSelect();
  else goToSetup();
});

menuTitleBtn.addEventListener('click', () => {
  audio.playSe('select');
  goToTitle();
});

// 背景クリックでも閉じる（パネル内クリックは対象外）
gameMenuEl.addEventListener('click', (e) => {
  if (e.target === gameMenuEl) {
    audio.playSe('select');
    closeMenu();
  }
});

// --- 観戦モード（CPU vs CPU）コントロール ---

function updateSpectateControls() {
  const spectate = flow.screen === 'game' && isSpectateMatch(settings.players);
  spectateControlsEl.classList.toggle('hidden', !spectate);
}

function updatePauseBtn() {
  pauseBtn.textContent = cpuDriver.paused ? '▶ 再開' : '⏸ 一時停止';
  pauseBtn.classList.toggle('paused', cpuDriver.paused);
}

pauseBtn.addEventListener('click', () => {
  audio.playSe('select');
  cpuDriver.setPaused(!cpuDriver.paused);
  updatePauseBtn();
  // 再開時は次のCPU手を予約し直す（一時停止中は maybeSchedule が張らない）
  driveCpu();
});

// 速度ボタン（遅い/ふつう/速い）を SPECTATE_SPEEDS から生成
{
  const speedBtns = [];
  for (const speed of SPECTATE_SPEEDS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = speed.label;
    btn.addEventListener('click', () => {
      audio.playSe('select');
      cpuDriver.setDelay(speed.ms);
      for (const b of speedBtns) b.classList.remove('selected');
      btn.classList.add('selected');
      driveCpu(); // 保留中のタイマーも新しい間隔で張り替える
    });
    if (speed.id === DEFAULT_SPEED_ID) btn.classList.add('selected');
    speedBtns.push(btn);
    speedButtonsEl.appendChild(btn);
  }
  cpuDriver.setDelay(speedById(DEFAULT_SPEED_ID).ms);
}

// --- ミュートトグル（状態は audio モジュールが localStorage に永続化） ---

function renderMuteBtn() {
  muteBtn.textContent = audio.muted ? '🔇' : '🔊';
  muteBtn.setAttribute('aria-pressed', audio.muted ? 'true' : 'false');
}
muteBtn.addEventListener('click', () => {
  audio.unlock(); // このクリック自体もユーザージェスチャーとして活用
  audio.setMuted(!audio.muted);
  renderMuteBtn();
});
renderMuteBtn();

// 入力環境の変化（タブレットへのマウス接続等）でガイド文言を更新
if (typeof coarsePointerQuery.addEventListener === 'function') {
  coarsePointerQuery.addEventListener('change', () => updateGuideBar());
}

// --- HUD 情報部の折りたたみ（状態は localStorage に永続化） ---
// 折りたたむのはプレイヤー情報・ギミック凡例・陣地凡例（#hud-body）のみ。
// 手番表示・能力/パスボタン・モード表示は常時表示（パスが唯一の合法手の
// とき折りたたみで詰まないように）。

const HUD_COLLAPSED_KEY = 'palisade-hud-collapsed';

function applyHudCollapsed(collapsed) {
  hudEl.classList.toggle('collapsed', collapsed);
  hudToggleBtn.textContent = collapsed ? '＋' : '−';
  hudToggleBtn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
  hudToggleBtn.setAttribute(
    'aria-label',
    collapsed ? '情報を表示' : '情報を折りたたむ',
  );
}

let hudCollapsed = false;
try {
  hudCollapsed = localStorage.getItem(HUD_COLLAPSED_KEY) === '1';
} catch {
  /* プライベートモード等では折りたたみ状態を永続化しない */
}
applyHudCollapsed(hudCollapsed);

hudToggleBtn.addEventListener('click', () => {
  hudCollapsed = !hudCollapsed;
  applyHudCollapsed(hudCollapsed);
  try {
    localStorage.setItem(HUD_COLLAPSED_KEY, hudCollapsed ? '1' : '0');
  } catch {
    /* ignore */
  }
});

// 凡例チップ(モバイルのみ表示): 狭幅では凡例パネルが下部操作を押し上げるので
// 既定は「?」チップに畳み、タップで開閉する。デスクトップではCSSがチップを
// 隠し常時展開のまま。
const legendsEl = document.getElementById('hud-legends');
const legendToggleBtn = document.getElementById('legend-toggle');
legendToggleBtn.addEventListener('click', () => {
  const open = legendsEl.classList.toggle('open');
  legendToggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
  legendToggleBtn.setAttribute('aria-label', open ? '凡例を閉じる' : '凡例を表示');
});

// --- ポインタ操作 ---
const raycaster = new THREE.Raycaster();
const pointer = new THREE.Vector2();

function setRayFromEvent(e) {
  const rect = canvas.getBoundingClientRect();
  pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
  pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  raycaster.setFromCamera(pointer, camera);
}

// 壁設置を試みる共通経路（マウス即設置 / タッチ2タップ目 / ✓ ボタン）。
// hideOnFail: 失敗時にプレビューを片づけるか（タッチ経路のみ true。
// マウスは従来どおり赤いホバープレビューを残す）。
function tryPlaceWall(slot, hideOnFail) {
  const move = moveForWallSlot(slot);
  if (inTutorial()) {
    // 正誤も不正手の説明もエンジンに委ねる（レッスン4の「置けない壁」体験）
    if (!isHumanTurn()) return;
    const res = submitTutorialMove(move);
    if (res && res.result !== 'advance' && hideOnFail) clearWallCandidate();
    return;
  }
  if (isHumanTurn() && isLegal(state, move)) {
    commitMove(move); // refresh() が候補とプレビューを片づける
  } else {
    audio.playSe('illegal');
    if (hideOnFail) clearWallCandidate();
  }
}

// ホバー: 通常モード=溝上で壁プレビュー / 壁除去モード=設置済み壁の強調
// マウス専用（タッチにはホバーがなく、候補プレビューを消してしまうため）。
// CPU手番・観戦中はプレビューを出さない（視点操作 OrbitControls は常に有効）
canvas.addEventListener('pointermove', (e) => {
  if (isTouchLike(e.pointerType)) return;
  if (!isHumanTurn()) {
    board.hideWallPreview();
    return;
  }
  // マウス操作を始めたらタッチ候補は破棄（Surface等の併用で表示と状態がずれないように）
  if (wallInput.candidate) clearWallCandidate();
  setRayFromEvent(e);

  if (abilityMode) {
    board.hideWallPreview();
    const ab = currentAbility();
    if (ab && ab.id === 'removeWall') {
      board.highlightWall(board.pickWall(raycaster));
    }
    return;
  }

  const slot = board.pickWallSlot(raycaster, lastOrientation);
  if (slot) {
    lastOrientation = slot.orientation;
    const move = { type: 'wall', at: slot.at, orientation: slot.orientation };
    board.showWallPreview(slot.at, slot.orientation, isLegal(state, move));
  } else {
    board.hideWallPreview();
  }
});

canvas.addEventListener('pointerdown', (e) => tapTracker.down(e));
canvas.addEventListener('pointercancel', () => tapTracker.cancel());

canvas.addEventListener('pointerup', (e) => {
  // ドラッグ（カメラ操作）とタップ/クリックを区別（閾値は pointerType 別）
  if (!tapTracker.up(e)) return; // カメラドラッグ: SEも鳴らさない
  if (!isHumanTurn()) return; // CPU手番・セットアップ中・勝敗確定後は入力ロック

  setRayFromEvent(e);

  // 能力モード: 対象（マス or 壁）タップで発動。対象が明確なので即実行のまま。
  if (abilityMode) {
    const ab = currentAbility();
    if (!ab) return;
    const target =
      ab.id === 'removeWall' ? board.pickWall(raycaster) : board.pickCell(raycaster);
    if (target) {
      const move =
        ab.id === 'removeWall' ? moveForWallPick(target) : moveForCellTap(target, ab);
      if (inTutorial()) submitTutorialMove(move);
      else if (isLegal(state, move)) commitMove(move);
      else audio.playSe('illegal');
    }
    return;
  }

  // 溝ピッキング（タッチは許容量を広げる）→ 状態機械が place/preview/cell を決める
  const touch = isTouchLike(e.pointerType);
  const picked = board.pickWallSlot(raycaster, lastOrientation, {
    slack: touch ? TOUCH_WALL_SLACK : undefined,
  });
  let slot = null;
  if (picked) {
    lastOrientation = picked.orientation;
    const move = { type: 'wall', at: picked.at, orientation: picked.orientation };
    slot = { at: picked.at, orientation: picked.orientation, legal: isLegal(state, move) };
  }
  const result = wallInput.tap(e.pointerType, slot);

  if (result.type === 'place') {
    tryPlaceWall(result.slot, touch);
    return;
  }
  if (result.type === 'illegal') {
    // 赤プレビューのまま確定操作: 設置せず音だけ（候補は維持）。
    // チュートリアルでは「なぜ置けないか」の説明をエンジンからもらう。
    if (inTutorial()) submitTutorialMove(moveForWallSlot(result.slot));
    else audio.playSe('illegal');
    updateWallConfirmUi();
    return;
  }
  if (result.type === 'preview') {
    board.showWallPreview(result.slot.at, result.slot.orientation, result.slot.legal);
    updateWallConfirmUi();
    return;
  }

  // result.type === 'cell': 候補が解除されたならプレビューも消してからマス処理へ
  if (result.cleared) {
    board.hideWallPreview();
    updateWallConfirmUi();
  }

  // マス上ならハイライト中のマスへの駒移動を試みる
  const cell = board.pickCell(raycaster);
  if (cell) {
    // 自分の駒マスは「選択のつもり」のタップなので何もしない
    const own = state.players[state.current].pawn;
    const ownTap = cell.col === own.col && cell.row === own.row;
    if (inTutorial()) {
      // どのマスでもエンジンに渡し、正誤・ヒントはエンジンに委ねる
      if (!ownTap) submitTutorialMove(moveForCellTap(cell));
    } else if (moves.some((m) => m.col === cell.col && m.row === cell.row)) {
      commitMove(moveForCellTap(cell));
    } else if (!ownTap) {
      audio.playSe('illegal');
    }
  }
});

canvas.addEventListener('pointerleave', (e) => {
  // タッチは離した後に pointerleave が来る（候補プレビューを消さない）
  if (isTouchLike(e.pointerType)) return;
  if (!wallInput.candidate) board.hideWallPreview();
  board.highlightWall(null);
});

// 確定UI: ✓ 置く（赤プレビュー中は illegal SE のみ）/ ✕ やめる
wallConfirmOkBtn.addEventListener('click', () => {
  const result = wallInput.confirm();
  if (result.type === 'place') {
    tryPlaceWall(result.slot, true);
  } else if (result.type === 'illegal') {
    if (inTutorial()) submitTutorialMove(moveForWallSlot(result.slot));
    else audio.playSe('illegal');
  }
});

wallConfirmCancelBtn.addEventListener('click', () => {
  audio.playSe('select');
  clearWallCandidate();
});

// 初期表示: タイトル画面（背景は初期配置のクラシック盤 + ゆっくり自動旋回）
applyFlowUi();
refresh();

// レンダーループ（dt でトゥイーンを駆動。タブ復帰時の暴走を防ぐため dt は上限クリップ）
let lastTime = performance.now();
function animate() {
  requestAnimationFrame(animate);
  const now = performance.now();
  const dt = Math.min(now - lastTime, 100);
  lastTime = now;
  resize();
  controls.update();
  board.update(dt);
  renderer.render(scene, camera);
}
animate();
